export type PropKind = 'remove' | 'undo' | 'shuffle' | 'revive';
export const MAIN_PROP_KINDS = ['remove', 'undo', 'shuffle'] as const;
export type MainPropKind = typeof MAIN_PROP_KINDS[number];
export type Difficulty = 'easy' | 'normal' | 'hard';
export type QuestionCategory = 'all' | 'math' | 'culture';
export const MATH_TOPIC_LABELS = {
  all: '混合练习', previous: '估算前期量', growth: '估算增长量', percent: '百分化计算',
  growthCompare: '增量比大小', previousCompare: '基期比大小', annualGrowth: '年均增长率',
  fractionBelow: '分数计算（分子＜分母）', fractionAbove: '分数计算（分子＞分母）',
  previousShare: '基期比重', fractionCompare: '分数比大小', annualAverage: '年平均量'
} as const;
export type MathTopic = keyof typeof MATH_TOPIC_LABELS;
export const MATH_TOPIC_GROUPS: { label: string; topics: Exclude<MathTopic, 'all'>[] }[] = [
  { label: '增长相关', topics: ['previous', 'growth', 'percent', 'growthCompare', 'previousCompare', 'annualGrowth'] },
  { label: '比重相关', topics: ['fractionBelow', 'fractionAbove', 'previousShare', 'fractionCompare', 'annualAverage'] }
];

export interface GameSettings {
  soundEnabled: boolean;
  musicEnabled: boolean;
  animationsEnabled: boolean;
  showRemaining: boolean;
  mode: 'props' | 'challenge';
  difficulty: Difficulty;
  props: Record<PropKind, number>;
  quizMode: 'off' | 'refill' | 'always';
  quizLimit: number;
  reviveQuizLimit: number;
  questionCategory: QuestionCategory;
  mathTopic: MathTopic;
}

export const DEFAULT_SETTINGS: GameSettings = {
  soundEnabled: true, musicEnabled: false, animationsEnabled: true, showRemaining: true,
  mode: 'props', difficulty: 'normal',
  props: { remove: 1, undo: 1, shuffle: 1, revive: 1 },
  quizMode: 'refill', quizLimit: 1, reviveQuizLimit: 1, questionCategory: 'all', mathTopic: 'all'
};

const integer = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(9, Math.max(0, Math.floor(value))) : fallback;
const boolean = (value: unknown, fallback: boolean) => typeof value === 'boolean' ? value : fallback;
const choice = <T extends string>(value: unknown, values: T[], fallback: T): T =>
  values.includes(value as T) ? value as T : fallback;

/** Validate storage, including legacy records containing only the two sound flags. */
export const normalizeSettings = (input: unknown): GameSettings => {
  const saved = input && typeof input === 'object' ? input as Partial<GameSettings> : {};
  const props: Partial<Record<PropKind, number>> = saved.props && typeof saved.props === 'object' ? saved.props : {};
  const legacyCategory = saved.questionCategory as string | undefined;
  return {
    soundEnabled: boolean(saved.soundEnabled, DEFAULT_SETTINGS.soundEnabled),
    musicEnabled: boolean(saved.musicEnabled, DEFAULT_SETTINGS.musicEnabled),
    animationsEnabled: boolean(saved.animationsEnabled, true),
    showRemaining: boolean(saved.showRemaining, true),
    mode: choice(saved.mode, ['props', 'challenge'], 'props'),
    difficulty: choice(saved.difficulty, ['easy', 'normal', 'hard'], 'normal'),
    props: {
      remove: integer(props.remove, 1), undo: integer(props.undo, 1),
      shuffle: integer(props.shuffle, 1), revive: integer(props.revive, 1)
    },
    quizMode: choice(saved.quizMode, ['off', 'refill', 'always'], 'refill'),
    quizLimit: integer(saved.quizLimit, DEFAULT_SETTINGS.quizLimit),
    reviveQuizLimit: integer(saved.reviveQuizLimit, DEFAULT_SETTINGS.reviveQuizLimit),
    questionCategory: choice(legacyCategory === 'science' ? 'culture' : legacyCategory, ['all', 'math', 'culture'], 'all'),
    mathTopic: choice(saved.mathTopic, Object.keys(MATH_TOPIC_LABELS) as MathTopic[], 'all')
  };
};

export const PROP_LABELS: Record<PropKind, string> = { remove: '移出', undo: '撤回', shuffle: '洗牌', revive: '复活' };
export const DIFFICULTY_LABELS: Record<Difficulty, string> = { easy: '简单', normal: '标准', hard: '地狱' };
export const CATEGORY_LABELS: Record<QuestionCategory, string> = { all: '混合', math: '计算练习', culture: '常识' };
